import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { startLoading, stopLoading } from "../../Redux/Screen/Action";
import PaymentApi from "Service/Payment.api";
import dayjs from "dayjs";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Button, Avatar, Chip, Card, InputAdornment
} from "@mui/material";
import { Send, HistoryEdu, Person, Payment } from "@mui/icons-material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

const ProfessionalPaymentProcess = () => {
  const navigate = useNavigate();
  const { profId } = useParams(); // From URL
  const location = useLocation(); // From navigate state
  const dispatch = useDispatch();

  // Data passed from the selection list page
  const profInfo = location.state?.prof;

  const [cases, setCases] = useState<any[]>([]);
  const [payments, setPayments] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (profId) fetchUnpaidCases();
  }, [profId]);

  const fetchUnpaidCases = async () => {
    dispatch(startLoading({ loading: true, loadtext: "Fetching cases..." }));
    try {
      const response = await PaymentApi.getProfessionalUnpaidCases(profId!);
      setCases(response.data || []);
    } catch (error) {
      console.error("Error fetching cases", error);
    } finally {
      dispatch(stopLoading());
    }
  };

  const handleAmountChange = (caseId: string, value: string) => {
    setPayments((prev) => ({ ...prev, [caseId]: value }));
  };

  const handleSubmitAll = async () => {
    const enteredCaseIds = Object.keys(payments).filter(id => payments[id] !== "");

    if (enteredCaseIds.length === 0) return alert("Please enter at least one amount");

    const hasInvalidAmount = enteredCaseIds.some(id => {
      const amount = parseFloat(payments[id]);
      return isNaN(amount) || amount <= 0;
    });

    if (hasInvalidAmount) return alert("Please enter a valid positive amount for every case.");

    const entries = enteredCaseIds.map(id => ({
      caseId: parseInt(id),
      amount: parseFloat(payments[id]),
      // adminNotes: `Admin processed on ${dayjs().format('YYYY-MM-DD')}`
    }));

    dispatch(startLoading({ loading: true, loadtext: "Submitting batch..." }));
    try {
      const payload = {
        professionalId: parseInt(profId!),
        entries: entries
      };
      await PaymentApi.submitBulkProfessionalPayments(payload);
      // alert("Submitted successfully!");
      setPayments({}); // Clear form
      fetchUnpaidCases(); // Refresh list
    } catch (error) {
      // alert("Submission failed.");
    } finally {
      dispatch(stopLoading());
    }
  };

  return (
    <Box p={4} sx={{ backgroundColor: "#f4f6f8", minHeight: "100vh", maxHeight: '80vh', overflow: "auto" }}>
      {/* 3. ADDED BACK BUTTON HERE */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate(-1)}
          sx={{ fontWeight: 'bold' }}
        >
          Back
        </Button>
      </Box>

      {/* HEADER SECTION - Now Dynamic */}
      <Paper elevation={0} sx={{
        p: 3,
        mb: 3,
        borderRadius: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexDirection: { xs: 'column', md: 'row' }, // Stack on mobile
        border: "1px solid #e0e0e0"
      }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
            {profInfo?.firstName ? profInfo.firstName[0] : <Person fontSize="large" />}
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {profInfo?.firstName} {profInfo?.lastName || "Professional Details"}
            </Typography>
            <Typography variant="body2" color="text.primary">
              Email: {profInfo?.email} | User ID: {profId}
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          size="large"
          startIcon={<Send />}
          onClick={handleSubmitAll}
          disabled={Object.keys(payments).length === 0}
          sx={{ borderRadius: 2, px: 4, textTransform: 'none', fontWeight: 'bold' }}
        >
          Submit for Request
        </Button>
      </Paper>

      {/* STATS SUMMARY - Now Dynamic */}
      <Box display="flex" gap={2} mb={3}>
        <Card sx={{ p: 2, flex: 1, textAlign: 'center' }}>
          <Typography color="text.primary" variant="caption" fontWeight="bold">PENDING CASES</Typography>
          <Typography variant="h4">{cases.length}</Typography>
        </Card>
        <Card sx={{ p: 2, flex: 1, textAlign: 'center' }}>
          <Typography color="text.primary" variant="caption" fontWeight="bold">TOTAL PAID VALUE</Typography>
          <Typography variant="h4" color="primary.main">
            ₹{Object.values(payments).reduce((sum, val) => sum + (Number(val) || 0), 0).toLocaleString()}
          </Typography>
        </Card>
      </Box>

      {/* MAIN DATA TABLE - Now Dynamic */}
      <TableContainer component={Paper} sx={{
        borderRadius: 2,
        maxHeight: '60vh',
        overflow: "auto",
        boxShadow: 2
      }}>
        <Table>
          <TableHead sx={{ backgroundColor: "#2a3162" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Case Details</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Completion Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '250px', color: '#fff' }}>Enter Payment Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cases.length === 0 ? (
              <TableRow><TableCell colSpan={4} align="center">No pending cases found for this professional.</TableCell></TableRow>
            ) : cases.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>
                  <Typography fontWeight="500">{item.title || item.caseTypeName}</Typography>
                  <Typography variant="caption" color="text.primary">ID: {item.id}</Typography>
                </TableCell>
                <TableCell>{dayjs(item.completedAt).format("DD MMM YYYY")}</TableCell>
                <TableCell>
                  <Chip label="Completed" color="success" size="small" icon={<HistoryEdu fontSize="small" />} />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="0.00"
                    type="number"
                    value={payments[item.id] || ""}
                    onChange={(e) => handleAmountChange(item.id, e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">
                        <Typography color="text.primary">₹</Typography>
                      </InputAdornment>
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ProfessionalPaymentProcess;